import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "sonner";
import { Key, Plus, History as HistoryIcon, Search, RotateCcw, Trash2, ArrowRight, AlertTriangle } from "lucide-react";
import { fmtRoDate } from "../../lib/dateFmt";

/**
 * Sectiune chei pentru un apartament (rental).
 * - Listă chei numerotate, grupate pe tip (apartament, poștă, etc — configurabile).
 * - Click pe o cheie → dialog cu istoric complet + acțiuni (alocă / returnează / pierdută).
 */
export default function ApartmentKeysPane({ rentalId, canEdit }) {
  const [keys, setKeys] = useState([]);
  const [types, setTypes] = useState([]);
  const [persons, setPersons] = useState([]);
  const [historical, setHistorical] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [manageTypesOpen, setManageTypesOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [k, t, p, h] = await Promise.all([
        api.get(`/rentals/${rentalId}/keys`),
        api.get(`/apartment-key-types`),
        api.get(`/persons`).catch(() => ({ data: [] })),
        api.get(`/historical-persons`).catch(() => ({ data: [] })),
      ]);
      setKeys(k.data || []);
      setTypes(t.data || []);
      setPersons((p.data || []).filter((x) => x.active !== false));
      setHistorical(h.data || []);
    } catch (e) {
      toast.error("Eroare la încărcarea cheilor");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rentalId]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const k of keys) {
      const tid = k.key_type_id || "—";
      if (!m.has(tid)) m.set(tid, { type_id: tid, name: k.key_type_name || "Cheie", icon: k.key_type_icon || "🔑", keys: [] });
      m.get(tid).keys.push(k);
    }
    return Array.from(m.values()).sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro"));
  }, [keys]);

  return (
    <div data-testid="apartment-keys-pane">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Key size={18} className="text-[var(--primary)]" />
        <h3 className="font-display text-lg font-bold flex-1">Chei apartament</h3>
        {canEdit && (
          <>
            <Button size="sm" variant="outline" onClick={() => setManageTypesOpen(true)} data-testid="manage-key-types-btn">
              ⚙ Tipuri
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)} className="bg-[var(--primary)]" data-testid="add-key-btn">
              <Plus size={14} className="mr-1"/> Adaugă chei
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 italic py-4">Se încarcă cheile…</div>
      ) : grouped.length === 0 ? (
        <div className="card-flat p-6 text-center text-zinc-500 italic">
          Niciuna încă. Apasă „Adaugă chei" pentru a începe.
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <div key={g.type_id} className="card-flat p-3">
              <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-600 mb-2 flex items-center gap-1.5">
                <span className="text-lg">{g.icon}</span> {g.name} ({g.keys.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {g.keys.map((k) => (
                  <KeyChip key={k.id} k={k} onOpen={() => setSelectedKey(k)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {addOpen && (
        <AddKeysDialog
          rentalId={rentalId}
          types={types}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}
      {manageTypesOpen && (
        <ManageKeyTypesDialog
          types={types}
          onClose={() => setManageTypesOpen(false)}
          onChanged={() => { load(); }}
        />
      )}
      {selectedKey && (
        <KeyDetailDialog
          keyItem={selectedKey}
          persons={persons}
          historical={historical}
          canEdit={canEdit}
          onClose={() => setSelectedKey(null)}
          onChanged={(updated) => {
            if (updated) setSelectedKey(updated);
            load();
          }}
          onHistoricalCreated={() => {
            // refresh historical list after quick-create
            api.get(`/historical-persons`).then((r) => setHistorical(r.data || []));
          }}
        />
      )}
    </div>
  );
}

function KeyChip({ k, onOpen }) {
  const isAssigned = k.status === "assigned";
  const isLost = k.status === "lost";
  const holderName = k.holder?.name;
  const cls = isLost
    ? "bg-red-100 border-red-400 text-red-800"
    : isAssigned
      ? "bg-emerald-50 border-emerald-400 text-emerald-900"
      : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50";
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-semibold transition-all hover:shadow-sm ${cls}`}
      title={isLost ? "Cheie pierdută" : isAssigned ? `Alocată: ${holderName}` : "În stoc"}
      data-testid={`key-chip-${k.id}`}
    >
      <span className="font-mono">#{k.number}</span>
      {isAssigned && holderName && (
        <span className="text-[10px] font-medium opacity-80">→ {holderName}</span>
      )}
      {isLost && <span className="text-[10px] font-bold uppercase">PIERDUTĂ</span>}
    </button>
  );
}

function AddKeysDialog({ rentalId, types, onClose, onSaved }) {
  const [typeId, setTypeId] = useState(types[0]?.id || "");
  const [count, setCount] = useState(1);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!typeId) { toast.error("Selectează un tip"); return; }
    if (count < 1) { toast.error("Cantitatea trebuie cel puțin 1"); return; }
    setSaving(true);
    try {
      const res = await api.post(`/rentals/${rentalId}/keys`, {
        key_type_id: typeId,
        count: parseInt(count) || 1,
        note: note.trim() || null,
      });
      const created = res.data || [];
      const nums = created.map((k) => `#${k.number}`).join(", ");
      toast.success(`${created.length} chei adăugate: ${nums}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la adăugare");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="add-keys-dialog">
        <DialogHeader><DialogTitle>Adaugă chei</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="small-label">Tip cheie</Label>
            <Select value={typeId} onValueChange={setTypeId}>
              <SelectTrigger className="mt-1" data-testid="add-keys-type"><SelectValue placeholder="Tip" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.icon || "🔑"} {t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="small-label">Câte chei? (numerotate consecutiv)</Label>
            <Input type="number" min={1} max={50} value={count} onChange={(e) => setCount(e.target.value)} data-testid="add-keys-count"/>
            <div className="text-[11px] text-zinc-500 mt-1">Se creează N chei consecutive începând cu următorul număr disponibil.</div>
          </div>
          <div>
            <Label className="small-label">Notă (opțional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: primite de la proprietar pe 01.02.2026" data-testid="add-keys-note"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--primary)]" data-testid="add-keys-save">
            {saving ? "Se adaugă…" : `Adaugă ${count || 1} chei`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageKeyTypesDialog({ types, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("🔑");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) { toast.error("Numele tipului obligatoriu"); return; }
    setSaving(true);
    try {
      await api.post(`/apartment-key-types`, { name: name.trim(), icon: icon.trim() || "🔑" });
      toast.success(`Tip „${name}" creat`);
      setName(""); setIcon("🔑");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Șterge tipul „${t.name}"? Va eșua dacă există chei de acest tip.`)) return;
    try {
      await api.delete(`/apartment-key-types/${t.id}`);
      toast.success("Șters"); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="manage-key-types-dialog">
        <DialogHeader><DialogTitle>Tipuri de chei</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="card-flat p-3">
            <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-600 mb-2">Existente</div>
            {types.length === 0 ? (
              <div className="text-zinc-400 italic text-xs">Niciun tip definit încă.</div>
            ) : (
              <div className="space-y-1.5">
                {types.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 px-2 py-1.5 bg-zinc-50 rounded">
                    <span className="text-lg">{t.icon || "🔑"}</span>
                    <span className="flex-1 font-medium">{t.name}</span>
                    {t.description && <span className="text-[11px] text-zinc-500 italic">{t.description}</span>}
                    <button onClick={() => del(t)} className="text-red-500 hover:bg-red-50 rounded p-1" data-testid={`del-key-type-${t.id}`} title="Șterge">
                      <Trash2 size={13}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="card-flat p-3">
            <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-600 mb-2">Adaugă nou</div>
            <div className="grid grid-cols-[60px_1fr_auto] gap-2">
              <Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="🔑" maxLength={2} className="text-center text-lg" data-testid="new-key-type-icon"/>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex: Cheie pivniță" data-testid="new-key-type-name"/>
              <Button onClick={create} disabled={saving} className="bg-[var(--primary)]" data-testid="new-key-type-save">Adaugă</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeyDetailDialog({ keyItem, persons, historical, canEdit, onClose, onChanged, onHistoricalCreated }) {
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);
  const [mode, setMode] = useState(null); // null | "assign" | "return" | "lost"
  const [pickPerson, setPickPerson] = useState("");
  const [pickHistorical, setPickHistorical] = useState("");
  const [pickKind, setPickKind] = useState("person"); // "person" | "historical"
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCreateHist, setShowCreateHist] = useState(false);
  const [newHistNume, setNewHistNume] = useState("");
  const [newHistPrenume, setNewHistPrenume] = useState("");

  const loadHistory = async () => {
    setLoadingHist(true);
    try {
      const r = await api.get(`/apartment-keys/${keyItem.id}/history`);
      setHistory(r.data || []);
    } catch { setHistory([]); }
    finally { setLoadingHist(false); }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [keyItem.id]);

  const submit = async () => {
    if (!mode) return;
    const payload = { action: mode, note: note.trim() || null };
    if (mode === "assign") {
      if (pickKind === "person") {
        if (!pickPerson) { toast.error("Selectează o persoană"); return; }
        payload.assignee_person_id = pickPerson;
      } else {
        if (!pickHistorical) { toast.error("Selectează un fost angajat"); return; }
        payload.assignee_historical_id = pickHistorical;
      }
    }
    setSaving(true);
    try {
      const r = await api.post(`/apartment-keys/${keyItem.id}/transfer`, payload);
      toast.success(mode === "assign" ? "Cheie alocată" : mode === "return" ? "Cheie returnată" : "Marcată pierdută");
      onChanged(r.data);
      await loadHistory();
      setMode(null); setPickPerson(""); setPickHistorical(""); setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSaving(false); }
  };

  const createHistorical = async () => {
    if (!newHistNume.trim()) { toast.error("Numele e obligatoriu"); return; }
    try {
      const r = await api.post(`/historical-persons`, {
        nume: newHistNume.trim(),
        prenume: newHistPrenume.trim() || null,
      });
      toast.success("Fost angajat înregistrat");
      setPickHistorical(r.data.id);
      setNewHistNume(""); setNewHistPrenume("");
      setShowCreateHist(false);
      onHistoricalCreated && onHistoricalCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const k = keyItem;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="key-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="text-2xl">{k.key_type_icon || "🔑"}</span>
            <span>{k.key_type_name} <span className="font-mono">#{k.number}</span></span>
            <span className="text-xs font-normal text-zinc-500">— {k.rental_label}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Stare curentă */}
          <div className={`card-flat p-3 ${k.status === "assigned" ? "bg-emerald-50 border-l-4 border-l-emerald-500" : k.status === "lost" ? "bg-red-50 border-l-4 border-l-red-500" : "bg-zinc-50 border-l-4 border-l-zinc-400"}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-600">Stare curentă</div>
            <div className="text-sm font-semibold mt-1">
              {k.status === "assigned" && k.holder ? (
                <>Deținută de <strong>{k.holder.name}</strong> {k.holder.kind === "historical" && <span className="text-[10px] uppercase font-bold text-zinc-500">(fost angajat)</span>} {k.holder.kind === "person_dismissed" && <span className="text-[10px] uppercase font-bold text-orange-700">(concediat — returnare pendentă)</span>}</>
              ) : k.status === "lost" ? (
                <span className="text-red-700">🚨 Pierdută</span>
              ) : (
                <span className="text-zinc-600">✓ În stoc</span>
              )}
            </div>
            {k.assigned_at && <div className="text-[11px] text-zinc-500 mt-0.5">de pe {fmtRoDate(k.assigned_at)}</div>}
            {k.note && <div className="text-[11px] italic text-zinc-600 mt-1">📝 {k.note}</div>}
          </div>

          {/* Acțiuni */}
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              {k.status !== "assigned" && (
                <Button size="sm" onClick={() => setMode("assign")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="key-action-assign">
                  <ArrowRight size={14} className="mr-1"/> Alocă cuiva
                </Button>
              )}
              {k.status === "assigned" && (
                <>
                  <Button size="sm" onClick={() => setMode("assign")} className="bg-blue-600 hover:bg-blue-700" data-testid="key-action-transfer">
                    <ArrowRight size={14} className="mr-1"/> Transferă altcuiva
                  </Button>
                  <Button size="sm" onClick={() => setMode("return")} variant="outline" data-testid="key-action-return">
                    <RotateCcw size={14} className="mr-1"/> Returnează (stoc)
                  </Button>
                </>
              )}
              {k.status !== "lost" && (
                <Button size="sm" onClick={() => setMode("lost")} variant="outline" className="border-red-300 text-red-700" data-testid="key-action-lost">
                  <AlertTriangle size={14} className="mr-1"/> Marcează pierdută
                </Button>
              )}
              {k.status === "lost" && (
                <Button size="sm" onClick={() => setMode("return")} variant="outline" data-testid="key-action-found">
                  ✓ Găsită (revine în stoc)
                </Button>
              )}
            </div>
          )}

          {/* Mode dialog inline */}
          {mode && (
            <div className="card-flat p-3 bg-blue-50/40 border-l-4 border-l-blue-500 space-y-3">
              <div className="text-[11px] uppercase tracking-wider font-bold text-blue-800">
                {mode === "assign" ? "Alocare nouă" : mode === "return" ? "Returnare în stoc" : "Marcare pierdere"}
              </div>
              {mode === "assign" && (
                <>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPickKind("person")}
                      className={`flex-1 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded border-2 ${pickKind === "person" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-700 border-blue-300"}`}
                      data-testid="pick-kind-person"
                    >
                      👤 Angajat curent
                    </button>
                    <button
                      type="button"
                      onClick={() => setPickKind("historical")}
                      className={`flex-1 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded border-2 ${pickKind === "historical" ? "bg-zinc-700 text-white border-zinc-700" : "bg-white text-zinc-700 border-zinc-300"}`}
                      data-testid="pick-kind-historical"
                    >
                      🕘 Fost angajat
                    </button>
                  </div>
                  {pickKind === "person" ? (
                    <Select value={pickPerson} onValueChange={setPickPerson}>
                      <SelectTrigger data-testid="pick-person"><SelectValue placeholder="Selectează angajat" /></SelectTrigger>
                      <SelectContent>
                        {persons.map((p) => <SelectItem key={p.id} value={p.id}>{p.nume} {p.prenume}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="space-y-2">
                      <Select value={pickHistorical} onValueChange={setPickHistorical}>
                        <SelectTrigger data-testid="pick-historical"><SelectValue placeholder="Selectează fost angajat" /></SelectTrigger>
                        <SelectContent>
                          {historical.length === 0 && (
                            <div className="px-3 py-2 text-xs text-zinc-500 italic">Niciun fost angajat înregistrat. Apasă „➕ Adaugă fost angajat".</div>
                          )}
                          {historical.map((h) => <SelectItem key={h.id} value={h.id}>{h.nume} {h.prenume || ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {!showCreateHist ? (
                        <button
                          type="button"
                          onClick={() => setShowCreateHist(true)}
                          className="text-xs text-blue-700 hover:underline"
                          data-testid="add-historical-toggle"
                        >
                          ➕ Adaugă fost angajat nou
                        </button>
                      ) : (
                        <div className="card-flat p-2 bg-white border border-zinc-200">
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Nume" value={newHistNume} onChange={(e) => setNewHistNume(e.target.value)} data-testid="new-hist-nume"/>
                            <Input placeholder="Prenume (opțional)" value={newHistPrenume} onChange={(e) => setNewHistPrenume(e.target.value)} data-testid="new-hist-prenume"/>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button size="sm" onClick={createHistorical} className="bg-zinc-800 hover:bg-zinc-900 text-white" data-testid="new-hist-save">Salvează</Button>
                            <Button size="sm" variant="outline" onClick={() => { setShowCreateHist(false); setNewHistNume(""); setNewHistPrenume(""); }}>Anulează</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              <div>
                <Label className="small-label">Notă (opțional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: predată la șantier, primită returnată în plic galben…" data-testid="key-action-note"/>
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => { setMode(null); setNote(""); setPickPerson(""); setPickHistorical(""); }} disabled={saving}>Anulează</Button>
                <Button size="sm" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="key-action-save">
                  {saving ? "Se salvează…" : "Confirmă"}
                </Button>
              </div>
            </div>
          )}

          {/* Istoric */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <HistoryIcon size={14} className="text-zinc-500"/>
              <h4 className="text-sm font-bold">Istoric cheie</h4>
            </div>
            {loadingHist ? (
              <div className="text-xs text-zinc-400 italic">Se încarcă…</div>
            ) : history.length === 0 ? (
              <div className="text-xs text-zinc-400 italic">Fără înregistrări.</div>
            ) : (
              <div className="space-y-1.5">
                {history.map((h, i) => {
                  const labels = { created: "🆕 Creată", assign: "→ Alocată", return: "↩ Returnată în stoc", lost: "🚨 Pierdută", found: "✓ Găsită", deleted: "🗑 Ștearsă" };
                  const colors = { created: "bg-zinc-100 text-zinc-700", assign: "bg-emerald-100 text-emerald-800", return: "bg-blue-100 text-blue-800", lost: "bg-red-100 text-red-800", found: "bg-green-100 text-green-800", deleted: "bg-zinc-200 text-zinc-600" };
                  const cls = colors[h.action] || "bg-zinc-100 text-zinc-700";
                  return (
                    <div key={h.id || i} className="text-[12px] flex items-start gap-2 px-2 py-1.5 rounded border border-zinc-200 bg-white" data-testid={`key-history-${i}`}>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${cls}`}>{labels[h.action] || h.action}</span>
                      <div className="flex-1 min-w-0">
                        {h.action === "assign" && (
                          <div>
                            {h.from_label ? <>De la <strong>{h.from_label}</strong> </> : null}
                            la <strong>{h.to_label || "—"}</strong>
                            {h.to_historical_id && <span className="text-[10px] ml-1 uppercase font-bold text-zinc-500">(fost angajat)</span>}
                          </div>
                        )}
                        {h.action === "return" && h.from_label && (
                          <div>De la <strong>{h.from_label}</strong> → în stoc</div>
                        )}
                        {h.note && <div className="text-[11px] italic text-zinc-600 mt-0.5">📝 {h.note}</div>}
                        <div className="text-[10px] text-zinc-500 mt-0.5">{h.by_user_name || h.by_user || "—"} · {fmtRoDate(h.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
